import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { 
  BookOpen, ArrowLeft, PenTool, Loader2, Check, 
  Lightbulb, AlertCircle, Star, Info, Sparkles, Quote
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Structure des sections de la dissertation avec conseils
const DISSERTATION_SECTIONS = {
  accroche: {
    title: "Accroche",
    placeholder: "Commencez par une accroche qui amene le sujet...",
    tips: [
      "Partez d'un exemple concret, d'une situation quotidienne",
      "Ou d'une citation d'un philosophe en lien avec le sujet",
      "Ou d'un paradoxe, une tension qui interpelle",
      "L'accroche doit naturellement amener a la question du sujet"
    ],
    minWords: 30
  },
  analyse_sujet: {
    title: "Analyse du sujet",
    placeholder: "Definissez les termes cles du sujet...",
    tips: [
      "Definissez CHAQUE terme important du sujet",
      "Montrez les differents sens possibles des mots",
      "Identifiez les presupposes du sujet (ce qu'il admet implicitement)",
      "Reperez le type de question (qu'est-ce que, peut-on, faut-il, etc.)"
    ],
    minWords: 60
  },
  problematique: {
    title: "Problematique",
    placeholder: "Formulez la problematique...",
    tips: [
      "La problematique n'est PAS la reformulation du sujet",
      "C'est la TENSION, le PROBLEME que le sujet souleve",
      "Montrez pourquoi la question n'a pas de reponse evidente",
      "Utilisez une formule comme: 'Le probleme est de savoir si...' ou 'La difficulte est que...'"
    ],
    minWords: 40
  },
  annonce_plan: {
    title: "Annonce du plan",
    placeholder: "Annoncez les trois parties de votre developpement...",
    tips: [
      "Annoncez clairement les 3 parties (these, antithese, synthese)",
      "Chaque partie doit repondre partiellement a la question",
      "Evitez 'Dans un premier temps... dans un second temps...'",
      "Preferez: 'Nous verrons d'abord que... cependant... enfin...'"
    ],
    minWords: 40
  },
  these_titre: {
    title: "I. These - Titre",
    placeholder: "Titre de votre premiere partie...",
    tips: [
      "Le titre doit etre une affirmation, pas une question",
      "Il resume l'idee principale de cette partie",
      "Exemple: 'La conscience comme specifite humaine'"
    ],
    minWords: 5
  },
  these_a: {
    title: "I.A - Premier argument",
    placeholder: "Developpez votre premier argument...",
    tips: [
      "Commencez par enoncer clairement l'idee",
      "Appuyez-vous sur un philosophe (nom + oeuvre + these)",
      "Donnez un exemple concret pour illustrer",
      "Concluez en montrant comment cet argument repond a la question"
    ],
    minWords: 80
  },
  these_b: {
    title: "I.B - Deuxieme argument",
    placeholder: "Developpez votre deuxieme argument...",
    tips: [
      "Cet argument doit APPROFONDIR le premier, pas le repeter",
      "Utilisez un autre philosophe ou un autre aspect",
      "Transition vers l'argument suivant"
    ],
    minWords: 80
  },
  these_c: {
    title: "I.C - Troisieme argument (optionnel)",
    placeholder: "Developpez un troisieme argument si necessaire...",
    tips: [
      "Ce troisieme argument n'est pas toujours necessaire",
      "Il peut montrer une limite qui amene l'antithese"
    ],
    minWords: 0
  },
  transition_1: {
    title: "Transition I → II",
    placeholder: "Transition vers la deuxieme partie...",
    tips: [
      "Resumez brievement ce qui vient d'etre dit",
      "Montrez les LIMITES de cette position",
      "Annoncez la nouvelle direction que vous allez prendre",
      "Utilisez des connecteurs: 'Cependant', 'Neanmoins', 'Toutefois'"
    ],
    minWords: 30
  },
  antithese_titre: {
    title: "II. Antithese - Titre",
    placeholder: "Titre de votre deuxieme partie...",
    tips: [
      "Le titre doit s'opposer ou nuancer la premiere partie",
      "Exemple: 'Les limites de la conscience humaine'"
    ],
    minWords: 5
  },
  antithese_a: {
    title: "II.A - Premier argument",
    placeholder: "Developpez votre premier contre-argument...",
    tips: [
      "Montrez en quoi la these precedente est insuffisante",
      "Appuyez-vous sur un philosophe DIFFERENT",
      "L'antithese n'est pas une negation totale mais une nuance"
    ],
    minWords: 80
  },
  antithese_b: {
    title: "II.B - Deuxieme argument",
    placeholder: "Developpez votre deuxieme contre-argument...",
    tips: [
      "Approfondissez la critique ou la nuance",
      "Exemples et references philosophiques"
    ],
    minWords: 80
  },
  antithese_c: {
    title: "II.C - Troisieme argument (optionnel)",
    placeholder: "Developpez un troisieme contre-argument si necessaire...",
    tips: [
      "Peut preparer la synthese en montrant une nouvelle voie"
    ],
    minWords: 0
  },
  transition_2: {
    title: "Transition II → III",
    placeholder: "Transition vers la troisieme partie...",
    tips: [
      "Montrez que ni la these ni l'antithese ne suffisent",
      "Annoncez une position qui depasse l'opposition"
    ],
    minWords: 30
  },
  synthese_titre: {
    title: "III. Synthese - Titre",
    placeholder: "Titre de votre troisieme partie...",
    tips: [
      "La synthese DEPASSE l'opposition these/antithese",
      "Elle ne dit pas 'un peu des deux'",
      "Elle propose une nouvelle perspective"
    ],
    minWords: 5
  },
  synthese_a: {
    title: "III.A - Premier element de synthese",
    placeholder: "Developpez votre premier element de synthese...",
    tips: [
      "Montrez comment reformuler le probleme",
      "Proposez une distinction conceptuelle qui resout la tension",
      "Appuyez-vous sur un philosophe qui permet ce depassement"
    ],
    minWords: 80
  },
  synthese_b: {
    title: "III.B - Deuxieme element de synthese",
    placeholder: "Developpez votre deuxieme element de synthese...",
    tips: [
      "Approfondissez votre position personnelle (argumentee)",
      "Montrez la fecondite de cette nouvelle perspective"
    ],
    minWords: 60
  },
  conclusion_bilan: {
    title: "Conclusion - Bilan",
    placeholder: "Resumez le parcours de votre reflexion...",
    tips: [
      "Reprenez brievement les 3 moments de votre reflexion",
      "Montrez comment vous avez repondu a la question",
      "Formulez clairement votre reponse finale"
    ],
    minWords: 50
  },
  conclusion_ouverture: {
    title: "Conclusion - Ouverture",
    placeholder: "Ouvrez sur une nouvelle question...",
    tips: [
      "L'ouverture prolonge la reflexion sans la contredire",
      "Elle peut etre une nouvelle question que votre reflexion souleve",
      "Ou un elargissement vers un autre domaine (art, politique, etc.)",
      "EVITEZ les ouvertures banales ('De nos jours...')"
    ],
    minWords: 30
  }
};

const DissertationModule = ({ user }) => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [selectedNotion, setSelectedNotion] = useState("");
  const [currentExercise, setCurrentExercise] = useState(null);
  const [sections, setSections] = useState(
    Object.keys(DISSERTATION_SECTIONS).reduce((acc, key) => ({ ...acc, [key]: "" }), {})
  );
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [aiHints, setAiHints] = useState(null);
  const [loadingHints, setLoadingHints] = useState(false);

  useEffect(() => {
    fetchTopics();
  }, []);

  const fetchTopics = async () => {
    try {
      const response = await axios.get(`${API}/program/dissertation-topics`);
      setTopics(response.data);
    } catch (error) {
      console.error("Error fetching topics:", error);
    }
  };

  const generateExercise = async () => {
    setLoading(true);
    setResult(null);
    setSections(
      Object.keys(DISSERTATION_SECTIONS).reduce((acc, key) => ({ ...acc, [key]: "" }), {})
    );
    setAiHints(null);
    
    try {
      const response = await axios.post(`${API}/exercises/generate`, {
        student_id: user.id,
        exercise_type: "dissertation",
        notion: selectedNotion && selectedNotion !== "all" ? selectedNotion : undefined
      });
      setCurrentExercise(response.data);
      toast.success("Sujet genere !");
      
      // Auto-fetch AI hints
      fetchAiHints(response.data);
    } catch (error) {
      console.error("Error generating exercise:", error);
      toast.error("Erreur lors de la generation");
    } finally {
      setLoading(false);
    }
  };

  const fetchAiHints = async (exercise) => {
    setLoadingHints(true);
    try {
      const response = await axios.post(`${API}/exercises/hints`, {
        exercise_id: exercise.id,
        exercise_type: "dissertation",
        topic: exercise.topic,
        notion: exercise.notion,
        student_id: user.id
      });
      setAiHints(response.data);
    } catch (error) {
      console.error("Error fetching hints:", error);
    } finally {
      setLoadingHints(false);
    }
  };

  const updateSection = (key, value) => {
    setSections(prev => ({ ...prev, [key]: value }));
  };

  const getTotalWords = () => {
    return Object.values(sections).join(" ").trim().split(/\s+/).filter(w => w).length;
  };

  const getSectionWordCount = (key) => {
    return sections[key].trim().split(/\s+/).filter(w => w).length;
  };

  const submitAnswer = async () => {
    const totalWords = getTotalWords();
    if (totalWords < 500) {
      toast.error("Votre dissertation doit contenir au moins 500 mots au total");
      return;
    }

    // Check required sections
    const requiredSections = ["accroche", "analyse_sujet", "problematique", "these_a", "antithese_a", "conclusion_bilan"];
    for (const key of requiredSections) {
      const config = DISSERTATION_SECTIONS[key];
      if (getSectionWordCount(key) < config.minWords) {
        toast.error(`La section "${config.title}" doit contenir au moins ${config.minWords} mots`);
        return;
      }
    }

    setSubmitting(true);
    
    // Combine all sections into formatted answer
    const formattedAnswer = Object.entries(sections)
      .filter(([key, value]) => value.trim())
      .map(([key, value]) => `## ${DISSERTATION_SECTIONS[key].title}\n\n${value}`)
      .join("\n\n---\n\n");
    
    try {
      const response = await axios.post(`${API}/exercises/submit`, {
        exercise_id: currentExercise.id,
        student_id: user.id,
        answer: formattedAnswer,
        sections: sections
      });
      setResult(response.data);
      toast.success("Dissertation evaluee !");
    } catch (error) {
      console.error("Error submitting:", error);
      toast.error("Erreur lors de l'evaluation");
    } finally {
      setSubmitting(false);
    }
  };

  const uniqueNotions = [...new Set(topics.map(t => t.notion))];

  // Group sections for display
  const sectionGroups = [
    { title: "Introduction", keys: ["accroche", "analyse_sujet", "problematique", "annonce_plan"] },
    { title: "I. These", keys: ["these_titre", "these_a", "these_b", "these_c", "transition_1"] },
    { title: "II. Antithese", keys: ["antithese_titre", "antithese_a", "antithese_b", "antithese_c", "transition_2"] },
    { title: "III. Synthese", keys: ["synthese_titre", "synthese_a", "synthese_b"] },
    { title: "Conclusion", keys: ["conclusion_bilan", "conclusion_ouverture"] }
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="dissertation-page">
      {/* Navigation */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            className="text-white hover:bg-white/10"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
            <PenTool className="w-5 h-5 text-white" />
          </div>
          <span className="navbar-logo hidden sm:block">Dissertation</span>
        </div>
      </nav>

      <main className="px-4 py-6 max-w-5xl mx-auto page-enter">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Module Dissertation
          </h1>
          <p className="text-[#64748B]">
            Construisez votre dissertation étape par étape avec SophIA
          </p>
        </div>

        {!currentExercise ? (
          <Card className="card-sjh" data-testid="topic-selector">
            <CardHeader>
              <CardTitle className="text-lg text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Choisir un sujet
              </CardTitle>
              <CardDescription>
                Selectionnez une notion ou laissez le hasard decider
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#0F172A] mb-2 block">
                  Notion (optionnel)
                </label>
                <Select value={selectedNotion} onValueChange={setSelectedNotion}>
                  <SelectTrigger className="border-[#E2E8F0]" data-testid="notion-select">
                    <SelectValue placeholder="Toutes les notions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les notions</SelectItem>
                    {uniqueNotions.map((notion, idx) => (
                      <SelectItem key={idx} value={notion}>{notion}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={generateExercise}
                disabled={loading}
                className="w-full bg-[#1A365D] hover:bg-[#132745]"
                data-testid="generate-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generation...
                  </>
                ) : (
                  <>
                    <PenTool className="w-4 h-4 mr-2" />
                    Générer un sujet
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        ) : result ? (
          <Card className="card-sjh" data-testid="result-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Evaluation de votre dissertation
                </CardTitle>
                <div className="flex items-center gap-2 bg-[#D4AF37]/10 px-4 py-2 rounded-lg">
                  <Star className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-xl font-bold text-[#1A365D]">{result.score}/20</span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Section-by-section feedback */}
              {result.section_feedback && (
                <Accordion type="multiple" className="space-y-2">
                  {Object.entries(result.section_feedback).map(([key, feedback]) => (
                    <AccordionItem key={key} value={key} className="border rounded-lg px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{DISSERTATION_SECTIONS[key]?.title || key}</span>
                          <Badge className={feedback.score >= 15 ? "bg-green-500" : feedback.score >= 10 ? "bg-yellow-500" : "bg-red-500"}>
                            {feedback.score}/20
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-[#64748B]">
                        {feedback.comment}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}

              {/* Strengths */}
              <div>
                <h3 className="flex items-center gap-2 font-semibold text-[#22C55E] mb-3">
                  <Check className="w-5 h-5" />
                  Points forts
                </h3>
                <ul className="space-y-2">
                  {result.strengths?.map((s, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-[#0F172A]">
                      <span className="text-[#22C55E]">•</span>
                      {s}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Improvements */}
              <div>
                <h3 className="flex items-center gap-2 font-semibold text-[#EAB308] mb-3">
                  <Lightbulb className="w-5 h-5" />
                  Axes d'amelioration
                </h3>
                <ul className="space-y-2">
                  {result.improvements?.map((i, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-[#0F172A]">
                      <span className="text-[#EAB308]">•</span>
                      {i}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Feedback */}
              <div className="bg-[#F8F9FA] rounded-lg p-4">
                <h3 className="font-semibold text-[#1A365D] mb-2">Commentaire du professeur</h3>
                <p className="text-sm text-[#64748B] whitespace-pre-wrap">{result.feedback}</p>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setCurrentExercise(null);
                    setResult(null);
                    setSections(
                      Object.keys(DISSERTATION_SECTIONS).reduce((acc, key) => ({ ...acc, [key]: "" }), {})
                    );
                    setAiHints(null);
                  }}
                  className="flex-1 border-[#1A365D] text-[#1A365D]"
                  data-testid="new-exercise-button"
                >
                  Nouveau sujet
                </Button>
                <Button 
                  onClick={() => navigate("/dashboard")}
                  className="flex-1 bg-[#1A365D] hover:bg-[#132745]"
                >
                  Retour au tableau de bord
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Subject Card */}
            <Card className="card-sjh" data-testid="exercise-card">
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-[#1A365D]">Dissertation</Badge>
                  <Badge className="bg-[#D4AF37] text-[#0F172A]">{currentExercise.notion}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-gradient-to-br from-[#1A365D] to-[#2D4A7C] text-white rounded-lg p-6">
                  <Quote className="w-8 h-8 text-[#D4AF37] mb-4" />
                  <h2 className="text-xl md:text-2xl font-semibold leading-relaxed" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {currentExercise.topic}
                  </h2>
                </div>
              </CardContent>
            </Card>

            {/* AI Hints Card */}
            {(loadingHints || aiHints) && (
              <Card className="card-sjh border-[#D4AF37]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-[#1A365D] flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                    Pistes de reflexion (generees par SophIA)
                  </CardTitle>
                  <CardDescription>
                    Ces pistes vous aident a demarrer, mais construisez VOTRE reflexion !
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingHints ? (
                    <div className="flex items-center gap-2 text-[#64748B]">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generation des pistes...
                    </div>
                  ) : aiHints && (
                    <div className="space-y-4">
                      {aiHints.definitions && (
                        <div>
                          <h4 className="font-semibold text-[#1A365D] text-sm mb-2">Definitions des termes cles</h4>
                          <ul className="text-sm text-[#64748B] space-y-1">
                            {aiHints.definitions.map((d, i) => (
                              <li key={i}>• <strong>{d.term}</strong>: {d.definition}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {aiHints.problematic_hints && (
                        <div>
                          <h4 className="font-semibold text-[#1A365D] text-sm mb-2">Pistes pour la problematique</h4>
                          <ul className="text-sm text-[#64748B] space-y-1">
                            {aiHints.problematic_hints.map((h, i) => (
                              <li key={i}>• {h}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {aiHints.suggested_authors && (
                        <div>
                          <h4 className="font-semibold text-[#1A365D] text-sm mb-2">Philosophes a mobiliser</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {aiHints.suggested_authors.map((a, i) => (
                              <div key={i} className="p-2 bg-[#F0F9FF] rounded text-sm">
                                <strong className="text-[#1A365D]">{a.name}</strong>
                                <p className="text-[#64748B] text-xs mt-1">{a.thesis}</p>
                                {a.work && <p className="text-[#64748B] text-xs italic">Oeuvre: {a.work}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {aiHints.plan_suggestion && (
                        <div>
                          <h4 className="font-semibold text-[#1A365D] text-sm mb-2">Suggestion de plan</h4>
                          <ol className="text-sm text-[#64748B] space-y-1 list-decimal list-inside">
                            {aiHints.plan_suggestion.map((p, i) => (
                              <li key={i}>{p}</li>
                            ))}
                          </ol>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Section-by-Section Input */}
            <Card className="card-sjh">
              <CardHeader>
                <CardTitle className="text-lg text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Rédigez votre dissertation
                </CardTitle>
                <CardDescription>
                  Complétez chaque section. Les parties sont regroupées pour plus de clarté.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" defaultValue={["Introduction"]} className="space-y-4">
                  {sectionGroups.map((group, groupIndex) => (
                    <AccordionItem 
                      key={group.title} 
                      value={group.title}
                      className="border-2 rounded-lg overflow-hidden"
                    >
                      <AccordionTrigger className="px-4 py-3 bg-[#1A365D] text-white hover:no-underline hover:bg-[#132745]">
                        <span className="font-semibold">{group.title}</span>
                      </AccordionTrigger>
                      <AccordionContent className="p-4 space-y-4">
                        {group.keys.map((key) => {
                          const config = DISSERTATION_SECTIONS[key];
                          return (
                            <div key={key} className="space-y-2">
                              <div className="flex items-center justify-between">
                                <label className="font-medium text-[#1A365D] text-sm flex items-center gap-2">
                                  <div className={`w-2 h-2 rounded-full ${
                                    getSectionWordCount(key) >= config.minWords ? 'bg-green-500' : 
                                    sections[key].trim() ? 'bg-yellow-500' : 'bg-gray-300'
                                  }`} />
                                  {config.title}
                                </label>
                                <span className="text-xs text-[#64748B]">
                                  {getSectionWordCount(key)} mots{config.minWords > 0 ? ` / min ${config.minWords}` : ' (optionnel)'}
                                </span>
                              </div>
                              
                              {/* Tips Box */}
                              <div className="p-2 bg-[#FEF3C7] rounded border border-[#FCD34D]">
                                <div className="flex items-start gap-2">
                                  <Info className="w-3 h-3 text-[#92400E] mt-0.5 flex-shrink-0" />
                                  <ul className="text-xs text-[#92400E] space-y-0.5">
                                    {config.tips.slice(0, 2).map((tip, i) => (
                                      <li key={i}>• {tip}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                              
                              <Textarea
                                value={sections[key]}
                                onChange={(e) => updateSection(key, e.target.value)}
                                placeholder={config.placeholder}
                                className={`min-h-[100px] border-[#E2E8F0] focus:border-[#1A365D] ${
                                  key.includes('titre') ? 'min-h-[50px]' : ''
                                }`}
                                data-testid={`section-${key}`}
                              />
                            </div>
                          );
                        })}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>

                {/* Submit Section */}
                <div className="mt-6 pt-4 border-t border-[#E2E8F0]">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <span className="text-sm font-medium text-[#1A365D]">Total : </span>
                      <span className={`text-sm font-bold ${getTotalWords() >= 500 ? 'text-green-600' : 'text-orange-500'}`}>
                        {getTotalWords()} mots
                      </span>
                      <span className="text-xs text-[#64748B] ml-2">(minimum 500)</span>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={submitAnswer}
                    disabled={submitting || getTotalWords() < 500}
                    className="w-full bg-[#D4AF37] hover:bg-[#B3932E] text-[#0F172A]"
                    data-testid="submit-button"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Evaluation en cours...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Soumettre ma dissertation
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default DissertationModule;
